import { ResponsiveTable } from '../common/ResponsiveTable';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, getAuthHeaders } from '../../config/api';
import { useThemedStyles } from '../../theme/useThemedStyles';
import { formatDate } from '../../utils/dateFormat';
import { ModalBackdrop } from '../common/ModalBackdrop';
import type { CalendarAppointmentDetail } from '../calendar/types';

const money = (value: number) => `Bs ${Number(value || 0).toFixed(2)}`;

export function ReportAppointmentDetails({ appointmentId, onClose }: { appointmentId: number; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const theme = useThemedStyles();
  const [detail, setDetail] = useState<CalendarAppointmentDetail | null>(null);
  const [failed, setFailed] = useState(false);
  const closeButton = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    const controller = new AbortController();
    setDetail(null);
    setFailed(false);
    fetch(getApiUrl(`/appointments/${appointmentId}/calendar-detail`), {
      headers: getAuthHeaders(localStorage.getItem('token') || ''), signal: controller.signal
    }).then(async response => {
      if (!response.ok) throw new Error('Appointment details unavailable');
      const payload = await response.json();
      if (!controller.signal.aborted) setDetail(payload);
    }).catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [appointmentId]);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeButton.current?.focus();
    return () => previousFocus?.focus();
  }, []);
  const outstanding = Number(detail?.financial.currentAppointment || 0) + Number(detail?.financial.currentReservation || 0);
  const red = theme.tokens.isLight ? '#b91c1c' : '#fca5a5';
  const cell = { padding: '10px 8px', textAlign: 'left' as const, borderBottom: `1px solid ${theme.border}` };

  return <ModalBackdrop className="responsive-row" onDismiss={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2600, background: '#0009', display: 'flex', justifyContent: 'flex-end' }}>
    <aside ref={dialog} role="dialog" aria-modal="true" aria-labelledby="report-appointment-title"
      style={{ width: 'min(820px, 96vw)', padding: 24, overflowY: 'auto', background: theme.surface, color: theme.text }}
      onKeyDown={event => {
        if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
        if (event.key === 'Tab') {
          const focusable = dialog.current?.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex="0"]');
          if (!focusable?.length) return;
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      }}>
      <header className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <h2 id="report-appointment-title">{t('reports.appointmentDetails', 'Appointment details')} #{appointmentId}</h2>
        <button ref={closeButton} type="button" onClick={onClose} style={{ ...theme.secondaryButton, padding: '8px 14px' }}>{t('common.close', 'Close')}</button>
      </header>
      {failed ? <p role="alert">{t('reports.appointmentLoadError', 'Could not load appointment details. Close and try again.')}</p> : !detail ?
        <p role="status">{t('common.loading', 'Loading...')}</p> : <>
          <dl className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 20px' }}>
            {[
              [t('common.patient', 'Patient'), detail.patient?.name || '-'],
              ['HC', detail.patient?.hc || '-'],
              [t('common.date', 'Date'), detail.appointment.startTime ? `${formatDate(detail.appointment.startTime)} ${new Date(detail.appointment.startTime).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}` : '-'],
              [t('reports.doctor', 'Doctor'), detail.doctor?.name || '-'],
              [t('doctorTreatments.assistant', 'Assistant'), detail.secondDoctor?.name || '-'],
              [t('common.status', 'Status'), detail.appointment.statusLabel || '-'],
              [t('calendar.room', 'Room'), detail.appointment.room ?? '-'],
              [t('calendar.notes', 'Notes'), detail.appointment.notes || '-']
            ].map(([label, value]) => <React.Fragment key={String(label)}><dt style={{ color: theme.muted }}>{label}</dt><dd style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</dd></React.Fragment>)}
          </dl>
          <p style={{ padding: 12, borderRadius: 6, background: outstanding > 0.005 ? 'rgba(239,68,68,0.12)' : theme.inputBg, color: outstanding > 0.005 ? red : theme.text }}>
            <strong>{t('reports.balanceDue', 'Balance due')}: {money(outstanding)}</strong>
          </p>
          <h3>{t('reports.treatments', 'Treatments')}</h3>
          <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}><ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{[t('common.treatment', 'Treatment'), t('doctorTreatments.total', 'Total'), t('reports.balanceDue', 'Balance due')].map(label => <th key={label} style={cell}>{label}</th>)}</tr></thead>
            <tbody>{detail.treatments.map(item => <tr key={item.treatmentId}>
              <td style={cell}>{item.treatmentName}{item.treatedTeeth && <small style={{ display: 'block' }}>{t('doctorTreatments.teeth', 'Teeth')}: {item.treatedTeeth}</small>}</td>
              <td style={cell}>{money(item.isIncludedFree ? 0 : Math.max(0, item.price * item.quantity - item.discount))}</td>
              <td style={{ ...cell, color: item.due > 0.005 ? red : theme.text }}>{money(item.due)}</td>
            </tr>)}</tbody>
          </table></ResponsiveTable></div>
          <h3>{t('doctorTreatments.paymentLedger', 'Payment ledger')}</h3>
          <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}><ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{[t('common.date', 'Date'), t('reports.paymentMethod', 'Method'), t('reports.amount', 'Amount')].map(label => <th key={label} style={cell}>{label}</th>)}</tr></thead>
            <tbody>{detail.payments.filter(item => item.appointmentId === appointmentId).map(item => <tr key={item.id}>
              <td style={cell}>{formatDate(item.date)}</td><td style={cell}>{item.method}</td><td style={cell}>{money(item.amount)}</td>
            </tr>)}</tbody>
          </table></ResponsiveTable></div>
        </>}
    </aside>
  </ModalBackdrop>;
}
